<template>

    <div class="login2">

        <div class="login2__logo-container">

            <img :src="computedLogoProvider" class="login2__logo">

        </div>

        <h2 class="login2__title mt__8">{{ $t('web_login_google_connect_title') }}</h2>

        <p class="login2__subtitle mt__8">{{ $t('web_login_google_connect_subtitle') }}</p>

        <label class="login2__label mt__24">{{ $t('web_login_google_connect_field_email') }}</label>

        <input v-model="inputEmail" class="login2__input mt__8">

        <div class="login2__label-container">

            <label class="login2__label mt__16">{{ $t('web_login_google_connect_field_ap') }}</label>

            <p class="login2__label-length">
                {{ $tc('web_login_google_connect_count_characters', 0, {number_characters: inputPassword.length}) }}</p>

        </div>

        <div class="login2__input-password-container mt__8">
            <the-mask v-model="inputPassword" :mask="['XXXX XXXX XXXX XXXX']" :type="inputPasswordType"
                      class="login2__input login2__input-yellow" v-on:keyup.enter="login"/>
            <img :src="computedEyeImg" class="login2__input-password-img" @click="updateShowPassword">
        </div>

        <div v-show="error" class="login2__error__container mt__8">
            <img class="login2__error__img" src="/img/icons/error-red.svg">
            <span class="login2__error__text">{{ computedMessageError }}</span>
        </div>

        <div class="login2__btn-container">

            <button class="btn btn-blue btn-big login2__btn mt__16" @click="TagClick('connect');login()">
                {{ $t('web_login_google_connect_cta') }}
            </button>

        </div>

        <page-loader ref="loader"></page-loader>

        <modal-error-provider ref="MEP" :email="inputEmail" :provider="provider"></modal-error-provider>

        <modal-error-imap ref="MEI" @close="showImapStep"></modal-error-imap>

        <modal-outlook-invalid-credentials ref="MOIC"></modal-outlook-invalid-credentials>

        <modal-error-application-password ref="MEAP" @close="showAppPassStep"></modal-error-application-password>

    </div>

</template>

<script>

import Vuex from 'vuex'
import {TheMask} from 'vue-the-mask'

import Api from '@/api'

import SessionStorage from '@/helpers/sessionStorage'

import Auth from '@/modules/auth'
import Tag from '@/modules/tag'

import ENUM from '@/assets/ENUM'

import InputText from "@/components/inputs/InputText"
import Markdown from '@/components/Markdown'
import ModalErrorApplicationPassword from '@/components/modal/ModalErrorApplicationPassword'
import ModalErrorProvider from '@/components/modal/ModalErrorProvider'
import PageLoader from "@/components/PageLoader"
import HeaderBand from '@/components/headers/HeaderBand'
import ModalErrorImap from '@/components/modal/ModalErrorImap'
import ModalOutlookInvalidCredentials from "@/components/modal/ModalOutlookInvalidCredentials"

export default {

    name: 'login',

    components: {
        InputText,
        Markdown,
        ModalErrorApplicationPassword,
        ModalErrorProvider,
        PageLoader,
        HeaderBand,
        ModalErrorImap,
        ModalOutlookInvalidCredentials,
        TheMask
    },

    watch: {

        inputEmail: function () {
            this.emailChange()
        }
    },

    computed: {

        ...Vuex.mapGetters(['page']),

        computedMessageError: function () {
            if (this.errorMessage === 'timeout') {

                return this.$t('fetch_error_text')

            } else {
                return this.$te(this.errorMessage) ? this.$t(this.errorMessage) : this.$t('login_error_invalid_email_password')

            }
        },

        computedLogoProvider: function () {
            return `/img/providers/${this.provider}.png`
        },

        testEmail: function () {
            return this.inputEmail.length > 0 && this.regexMail.test(this.inputEmail)
        },

        testPassword: function () {
            return this.inputPassword.length === 16
        },

        computedEyeImg: function () {
            return this.showPassword ? '/img/icons/eye-close-black.svg' : '/img/icons/eye-open-black.svg'
        },

        inputPasswordType: function () {
            return this.showPassword ? 'text' : 'password'
        }

    },

    props: {

        provider: {
            type: String,
            default: ''
        },

    },

    data() {
        return {
            regexMail: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            error: false,
            errorMessage: '',
            inputEmail: '',
            inputPassword: '',
            showPassword: true
        }
    },

    mounted() {
        const loginEmail = SessionStorage.getItem('email')
        if (loginEmail) {
            this.inputEmail = loginEmail
        }
    },

    methods: {

        ...Vuex.mapActions([
            'setEmail'
        ]),

        login() {

            if (this.testForm()) {
                this.error = false

                this.$refs.loader.start()

                const email = this.inputEmail
                const password = this.inputPassword

                SessionStorage.setItem('email', email)

                Auth.connect(this, email, password, this.provider)
                    .then(response => {
                        Tag.infoAll(this, 'status_final_connection', this.provider, Auth.Statuses.SUCCESS)
                        if (response.status === ENUM.LOGIN_STATUS.CREATED) {
                            this.$router.push({path: '/fetch'})
                                .catch(() => {})
                        } else {
                            this.$router.push({path: '/newsletters'})
                                .catch(() => {})
                        }

                    })
                    .catch(error => {
                        this.$refs.loader.stop()
                        this.error = true

                        const errorStatus = Auth.errorToStatus(error.message)
                        Tag.infoAll(this, 'status_final_connection', this.provider, errorStatus)

                        if (error.message) {

                            if (this.provider === 'outlook' && error.message === 'invalid_credentials') {

                                this.$refs.MOIC.open()

                            } else if (error.message === 'mx_unsupported' || error.message === 'mx_not_found') {

                                this.$refs.MEP.show(error)

                            } else {

                                this.error = true
                                this.isErrorPassword = true

                                if (error.message === 'mailbox_already_associated') {
                                    this.errorMessage = 'login_email_already_associated'
                                } else {

                                    this.errorMessage = error.message

                                    Tag.infoAll(this, 'web_login_gmail_invalid_credentials')

                                    switch (error.message) {
                                        case 'application_password':
                                            this.$refs.MEAP.open()
                                            break
                                        case 'imap_disabled':
                                            this.$refs.MEI.open()
                                            break
                                        case 'timeout':
                                            this.$refs.MLLF.open(this.provider)
                                            break
                                    }

                                }

                            }

                        } else {
                            this.errorMessage = ''
                        }
                    })

            } else {
                this.error = true
            }

        },

        testForm: function () {
            if (!this.testPassword && this.inputPassword.length > 0) {
                this.errorMessage = 'web_login_google_connect_error_app_password'
                this.error = true
                Tag.infoAll(this, 'web_login_google_error_app_password')
                this.$refs.MEAP.open()
            }
            return this.testEmail && this.testPassword
        },

        updateShowPassword() {
            this.showPassword = !this.showPassword
        },

        computedPasswordPlaceholder() {
            const str = this.appPass ? 'login_application_password_placeholder' : 'login_password_placeholder'
            return this.$t(str)
        },

        emailChange() {

            const email = this.inputEmail

            if (this.regexMail.test(email)) {

                SessionStorage.setItem('email', email)

                Api.provider.get(email)
                    .then(data => {

                        this.$router.push({path: `/login/${data.provider}`})

                    })
                    .catch(error => {

                        if (error.message === 'mx_unsupported' || error.message === 'mx_not_found') {

                            this.$refs.MEP.show(error)
                        }
                    })
            }
        },

        showAppPassStep() {
            this.$emit('showAppPassStep')
        },

        showImapStep() {
            this.$emit('showImapStep')
        },

        TagClick(name) {
            Tag.clickAll(this, name, this.provider)
        }

    }
}
</script>

<style lang="scss" scoped>

.login2 {
    width: 100%;
    padding: 32px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    &__logo-container {
        width: 192px;
        height: 64px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__logo {
        width: 130px;
        object-fit: contain;
    }

    &__title {
        font-weight: 600;
        line-height: 1.09;
        color: $charcoal;
    }

    &__subtitle {
        font-size: 17px;
        line-height: 1.41;
        color: $moon;
    }

    &__label {
        width: 100%;
        font-size: 17px;
        font-weight: 600;
        line-height: 1.15;
        text-align: left;
        color: $charcoal;
    }

    &__label-container {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: baseline;
        justify-content: space-between;
    }

    &__label-length {
        font-size: 12px;
        font-weight: 600;
        line-height: 1.33;
        color: $stone;
        white-space: nowrap;
    }

    &__input {
        width: 100%;
        padding: 22px 24px;
        border-radius: 56px;
        border: solid 1px #e7e7e8;
        background-color: $timberwolf;

        font-size: 17px;
        font-weight: 500;
        line-height: 1.18;
        text-align: left;
        color: $charcoal;
    }

    &__input-yellow {
        background-color: $sunglow;
    }

    &__input-password-container {
        display: flex;
        width: 100%;
        position: relative;
        align-items: center;
        justify-content: center;
    }

    &__input-password-img {
        position: absolute;
        right: 24px;
        width: 24px;
        object-fit: contain;
    }

    &__error {

        &__container {
            width: 100%;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
        }

        &__img {
            width: 20px;
            object-fit: contain;
            margin-right: 8px;
        }

        &__text {
            font-size: 14px;
            line-height: 1.14;
            text-align: left;
            color: $raspberry;
        }
    }

    &__btn-container {
        width: 100%;
        display: flex;
    }

    &__btn {
        @media screen and (min-width: $above-tablet-length) {
            padding: 24px 32px;
            width: fit-content;
        }
    }
}

</style>
